import { i18n } from '@/main'
import type { Contract } from '@/types/contracts'
import type { Company, Me, Profile } from '@/types/me'
import { EntityType, ProfileType } from '@/types/me'
import { ProductType } from '@/types/productTypes'
import { brokerName } from '@/utils/brokers'

export const CLAIMS_DECLARATION_BASE_URL = 'https://claims.yago.be'

type Locale = 'fr' | 'nl' | 'en'

enum Gender {
  Male = 'male',
  Female = 'female'
}

enum PolicyOwnerType {
  Individual = 'individual',
  SelfEmployed = 'self_employed',
  Company = 'company'
}

const CLAIMS_CIVILITY_I18N_KEY = 'modals.declare_claim.civility'

const civilityTitle = (gender: Me['gender'], locale: Locale): string | undefined => {
  if (gender !== Gender.Male && gender !== Gender.Female) return undefined
  /** @ts-ignore */
  return i18n.global.t(`${CLAIMS_CIVILITY_I18N_KEY}.${gender}`, {}, { locale })
}

const fullAddress = (address: Me['address']): string | undefined => {
  if (!address) return undefined
  const { street, streetNumber, zipCode, city } = address
  if (!street && !streetNumber && !zipCode && !city) return undefined

  return `${street ?? ''} ${streetNumber ?? ''}, ${zipCode ?? ''} ${city ?? ''}`.trim()
}

const policyOwnerType = (profile: Profile | null, company: Company | null): PolicyOwnerType => {
  if (profile?.profileType !== ProfileType.B2B) return PolicyOwnerType.Individual
  if (company?.entityType === EntityType.SoleProprietorship) return PolicyOwnerType.SelfEmployed
  return PolicyOwnerType.Company
}

const contractBrand = (contract: Contract): string | undefined => {
  if (contract.productType !== ProductType.Car) return undefined
  return 'brand' in contract ? contract.brand : undefined
}

const contractCovers = (contract: Contract): string | undefined => {
  const covers = contract.covers?.flatMap((belongingCover) => belongingCover.selected)

  return covers && covers.length > 0 ? covers.join(',') : undefined
}

export interface ClaimsUrlContext {
  contract: Contract
  me: Me | null
  company: Company | null
  profile: Profile | null
  pcaEmail: string | null
  locale: Locale
}

interface ClaimsUrlParams {
  portfolio_id: string
  lang: Locale
  title: string | undefined
  first_name: string | undefined
  last_name: string | undefined
  email: string | undefined
  phone_number: string | undefined
  full_address: string | undefined
  policy_owner_type: PolicyOwnerType
  vat_number: string | undefined
  company_legal_name: string | undefined
  pca_operator_email: string | undefined
  contract_number: string
  insurer: string
  product_line: string
  brand: string | undefined
  covers: string | undefined
}

export const buildClaimsDeclarationUrl = ({
  contract,
  me,
  company,
  profile,
  pcaEmail,
  locale
}: ClaimsUrlContext): string => {
  const isB2b = profile?.profileType === ProfileType.B2B

  const params: ClaimsUrlParams = {
    portfolio_id: brokerName(),
    lang: locale,
    title: civilityTitle(me?.gender ?? null, locale),
    first_name: isB2b ? profile?.firstName : me?.firstName,
    last_name: isB2b ? profile?.lastName : me?.lastName,
    email: isB2b ? profile?.email : me?.email,
    phone_number: me?.mobilePhone ?? undefined,
    full_address: fullAddress(me?.address ?? null),
    policy_owner_type: policyOwnerType(profile, company),
    vat_number: contract.policyOwner.vatNumber ?? company?.vatNumber,
    company_legal_name: company?.companyName,
    pca_operator_email: pcaEmail ?? undefined,
    contract_number: contract.reference,
    insurer: contract.insurer.logoAssetKey,
    product_line: contract.productLineId,
    brand: contractBrand(contract),
    covers: contractCovers(contract)
  }

  const url = new URL(CLAIMS_DECLARATION_BASE_URL)
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value ?? '')
  })

  return url.toString()
}
